import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { Lead, Interaction } from '../types/lead';
import { CalendarEvent } from '../types/calendar';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
// =====================================================================
// DataStoreContext — workspace compartido vía Supabase
// =====================================================================
// - Lee/escribe contra Supabase (tablas: leads, calendar_events,
//   timeline_events, qa_records) con realtime para sincronizar entre
//   dispositivos en vivo.
// - Mantiene una caché en localStorage como fallback offline.
// - Si Supabase no está configurado, funciona en modo local puro.
// =====================================================================
const STORAGE_KEY = 'growthly_data_v1';
interface QaRecord {
  id: string;
  leadId: string;
  date: string;
  score: number;
  notes: string;
}
interface DataStoreState {
  leads: Lead[];
  events: CalendarEvent[];
  timeline: Interaction[];
  qaRecords: QaRecord[];
}
const initialState: DataStoreState = {
  leads: [],
  events: [],
  timeline: [],
  qaRecords: []
};
interface DataStoreContextType extends DataStoreState {
  isSyncing: boolean;
  isCloudConnected: boolean;
  createLead: (lead: Partial<Lead>) => Lead;
  updateLead: (id: string, patch: Partial<Lead>) => void;
  deleteLead: (id: string) => void;
  createEvent: (event: Omit<CalendarEvent, 'id'>) => void;
  updateEvent: (id: string, patch: Partial<CalendarEvent>) => void;
  deleteEvent: (id: string) => void;
  addTimelineEvent: (event: Omit<Interaction, 'id'>) => void;
  createQaRecord: (record: Omit<QaRecord, 'id'>) => void;
  resetAll: () => void;
}
const DataStoreContext = createContext<DataStoreContextType | undefined>(undefined);
// ---------------------------------------------------------------------
// Mappers: app shape <-> DB shape
// ---------------------------------------------------------------------
function leadToRow(l: Lead) {
  // Campos "extra" que no tienen columna dedicada
  const extra = {
    ENLACE_GMB: l.ENLACE_GMB,
    MICRO_ACCION_TYPE: l.MICRO_ACCION_TYPE,
    MICRO_ACCION_DATE: l.MICRO_ACCION_DATE,
    CANAL_CONFIRMACION: l.CANAL_CONFIRMACION,
    TEXTO_EVIDENCIA: l.TEXTO_EVIDENCIA,
    LAST_CONTACT: l.LAST_CONTACT,
    NEXT_ACTION_DATE: l.NEXT_ACTION_DATE,
    hasPropietarioVisible: l.hasPropietarioVisible,
    hasBuenVolumenResenas: l.hasBuenVolumenResenas
  };
  return {
    id: l.id,
    nombre_negocio: l.NOMBRE_NEGOCIO || '',
    decisor_nombre: l.DECISOR_NOMBRE || '',
    decisor_cargo: l.DECISOR_CARGO || '',
    telefono_whatsapp: l.TELEFONO_WHATSAPP || '',
    verificado: !!l.VERIFICADO,
    direccion: l.DIRECCION || {},
    tag_geo: l.TAG_GEO || '',
    rrss_handles: l.RRSS_HANDLES || {},
    estado_web_menu: l.ESTADO_WEB_MENU || 'No web',
    menu_status: l.MENU_STATUS || 'Papel',
    nota_carencia: l.NOTA_CARENCIA || '',
    evidence_attachments: l.EVIDENCE_ATTACHMENTS || [],
    lead_score: l.LEAD_SCORE || 0,
    icp_priority: l.ICP_PRIORITY || 'C',
    lead_source: l.LEAD_SOURCE || 'Otro',
    enrichment_ready: !!l.ENRICHMENT_READY,
    owner_id: l.OWNER_ID || 'IVANA',
    state: l.STATE || 'Nuevo',
    evidence_completeness: l.EVIDENCE_COMPLETENESS || 0,
    extra,
    created_at: l.CREATED_AT || new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
}
function rowToLead(r: any): Lead {
  const extra = r.extra || {};
  return {
    id: r.id,
    NOMBRE_NEGOCIO: r.nombre_negocio || '',
    DECISOR_NOMBRE: r.decisor_nombre || '',
    DECISOR_CARGO: r.decisor_cargo || '',
    TELEFONO_WHATSAPP: r.telefono_whatsapp || '',
    VERIFICADO: !!r.verificado,
    DIRECCION: r.direccion || {
      calle: '',
      numero: '',
      barrio: '',
      ciudad: ''
    },
    TAG_GEO: r.tag_geo || '',
    ENLACE_GMB: extra.ENLACE_GMB,
    RRSS_HANDLES: r.rrss_handles || {},
    ESTADO_WEB_MENU: r.estado_web_menu || 'No web',
    MENU_STATUS: r.menu_status || 'Papel',
    NOTA_CARENCIA: r.nota_carencia || '',
    MICRO_ACCION_TYPE: extra.MICRO_ACCION_TYPE,
    MICRO_ACCION_DATE: extra.MICRO_ACCION_DATE,
    CANAL_CONFIRMACION: extra.CANAL_CONFIRMACION,
    TEXTO_EVIDENCIA: extra.TEXTO_EVIDENCIA,
    EVIDENCE_ATTACHMENTS: r.evidence_attachments || [],
    LEAD_SCORE: r.lead_score || 0,
    ICP_PRIORITY: r.icp_priority || 'C',
    LEAD_SOURCE: r.lead_source || 'Otro',
    ENRICHMENT_READY: !!r.enrichment_ready,
    LAST_CONTACT: extra.LAST_CONTACT,
    NEXT_ACTION_DATE: extra.NEXT_ACTION_DATE,
    OWNER_ID: r.owner_id || 'IVANA',
    STATE: r.state || 'Nuevo',
    EVIDENCE_COMPLETENESS: r.evidence_completeness || 0,
    CREATED_AT: r.created_at || new Date().toISOString(),
    UPDATED_AT: r.updated_at || new Date().toISOString(),
    hasPropietarioVisible: extra.hasPropietarioVisible,
    hasBuenVolumenResenas: extra.hasBuenVolumenResenas
  };
}
function eventToRow(e: CalendarEvent) {
  const extra = {
    leadName: e.leadName,
    attendees: e.attendees || [],
    organizer: e.organizer,
    description: e.description
  };
  return {
    id: e.id,
    lead_id: e.leadId || null,
    title: e.title || '',
    start_time: e.start,
    end_time: e.end || e.start,
    type: e.type || 'demo',
    status: e.status || 'confirmed',
    notes: e.description || '',
    extra
  };
}
function rowToEvent(r: any): CalendarEvent {
  const extra = r.extra || {};
  return {
    id: r.id,
    leadId: r.lead_id || '',
    leadName: extra.leadName || '',
    title: r.title || '',
    type: r.type || 'Demo',
    start: r.start_time,
    end: r.end_time || r.start_time,
    attendees: extra.attendees || [],
    organizer: extra.organizer || '',
    status: r.status || 'confirmed',
    description: extra.description || r.notes || ''
  };
}
function interactionToRow(i: Interaction) {
  return {
    id: i.id,
    lead_id: i.leadId,
    type: i.type,
    channel: '',
    content: i.content || '',
    date: i.timestamp || new Date().toISOString(),
    extra: {
      userId: i.userId
    }
  };
}
function rowToInteraction(r: any): Interaction {
  return {
    id: r.id,
    leadId: r.lead_id,
    type: r.type,
    content: r.content || '',
    timestamp: r.date || r.created_at,
    userId: r.extra && r.extra.userId || 'IVANA'
  };
}
function qaToRow(q: QaRecord) {
  return {
    id: q.id,
    lead_id: q.leadId,
    date: q.date || new Date().toISOString(),
    score: q.score || 0,
    notes: q.notes || ''
  };
}
function rowToQa(r: any): QaRecord {
  return {
    id: r.id,
    leadId: r.lead_id,
    date: r.date || r.created_at,
    score: r.score || 0,
    notes: r.notes || ''
  };
}
// ---------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------
export function DataStoreProvider({
  children
}: {
  children: ReactNode;
}) {
  const [data, setData] = useState<DataStoreState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initialState;
    } catch {
      return initialState;
    }
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCloudConnected, setIsCloudConnected] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;
  // Persistir caché local en cada cambio
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* ignore quota errors */
    }
  }, [data]);
  // -------------------------------------------------------------------
  // Carga inicial desde Supabase + suscripciones realtime
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;
    let cancelled = false;
    setIsSyncing(true);
    const loadAll = async () => {
      try {
        const [leadsRes, eventsRes, timelineRes, qaRes] = await Promise.all([supabase.from('leads').select('*').order('created_at', {
          ascending: false
        }), supabase.from('calendar_events').select('*').order('start_time', {
          ascending: true
        }), supabase.from('timeline_events').select('*').order('created_at', {
          ascending: false
        }), supabase.from('qa_records').select('*').order('created_at', {
          ascending: false
        })]);
        if (cancelled) return;
        const errors = [leadsRes.error, eventsRes.error, timelineRes.error, qaRes.error].filter(Boolean);
        if (errors.length) {
          console.error('[Growthly] Error cargando datos:', errors);
        }
        setData({
          leads: (leadsRes.data || []).map(rowToLead),
          events: (eventsRes.data || []).map(rowToEvent),
          timeline: (timelineRes.data || []).map(rowToInteraction),
          qaRecords: (qaRes.data || []).map(rowToQa)
        });
        setIsCloudConnected(true);
      } catch (err) {
        console.error('[Growthly] Fallo de conexión a Supabase:', err);
        setIsCloudConnected(false);
      } finally {
        if (!cancelled) setIsSyncing(false);
      }
    };
    loadAll();
    // ----- Realtime subscriptions ------------------------------------
    const channel = supabase.channel('growthly-shared-workspace').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'leads'
    }, payload => {
      setData(prev => {
        if (payload.eventType === 'DELETE') {
          const id = (payload.old as any)?.id;
          return {
            ...prev,
            leads: prev.leads.filter(l => l.id !== id)
          };
        }
        const row = rowToLead(payload.new as any);
        const exists = prev.leads.some(l => l.id === row.id);
        return {
          ...prev,
          leads: exists ? prev.leads.map(l => l.id === row.id ? row : l) : [row, ...prev.leads]
        };
      });
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'calendar_events'
    }, payload => {
      setData(prev => {
        if (payload.eventType === 'DELETE') {
          const id = (payload.old as any)?.id;
          return {
            ...prev,
            events: prev.events.filter(e => e.id !== id)
          };
        }
        const row = rowToEvent(payload.new as any);
        const exists = prev.events.some(e => e.id === row.id);
        return {
          ...prev,
          events: exists ? prev.events.map(e => e.id === row.id ? row : e) : [...prev.events, row]
        };
      });
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'timeline_events'
    }, payload => {
      setData(prev => {
        if (payload.eventType === 'DELETE') {
          const id = (payload.old as any)?.id;
          return {
            ...prev,
            timeline: prev.timeline.filter(t => t.id !== id)
          };
        }
        const row = rowToInteraction(payload.new as any);
        const exists = prev.timeline.some(t => t.id === row.id);
        return {
          ...prev,
          timeline: exists ? prev.timeline.map(t => t.id === row.id ? row : t) : [row, ...prev.timeline]
        };
      });
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'qa_records'
    }, payload => {
      setData(prev => {
        if (payload.eventType === 'DELETE') {
          const id = (payload.old as any)?.id;
          return {
            ...prev,
            qaRecords: prev.qaRecords.filter(q => q.id !== id)
          };
        }
        const row = rowToQa(payload.new as any);
        const exists = prev.qaRecords.some(q => q.id === row.id);
        return {
          ...prev,
          qaRecords: exists ? prev.qaRecords.map(q => q.id === row.id ? row : q) : [row, ...prev.qaRecords]
        };
      });
    }).subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);
  // -------------------------------------------------------------------
  // Mutations — escriben en Supabase (si está configurado) y en local
  // -------------------------------------------------------------------
  const createLead = (lead: Partial<Lead>): Lead => {
    const newLead: Lead = {
      id: `L-${Date.now()}`,
      NOMBRE_NEGOCIO: lead.NOMBRE_NEGOCIO || '',
      DECISOR_NOMBRE: lead.DECISOR_NOMBRE || '',
      DECISOR_CARGO: lead.DECISOR_CARGO || '',
      TELEFONO_WHATSAPP: lead.TELEFONO_WHATSAPP || '',
      VERIFICADO: lead.VERIFICADO || false,
      DIRECCION: lead.DIRECCION || {
        calle: '',
        numero: '',
        barrio: '',
        ciudad: ''
      },
      TAG_GEO: lead.TAG_GEO || '',
      RRSS_HANDLES: lead.RRSS_HANDLES || {},
      ESTADO_WEB_MENU: lead.ESTADO_WEB_MENU || 'No web',
      MENU_STATUS: lead.MENU_STATUS || 'Papel',
      NOTA_CARENCIA: lead.NOTA_CARENCIA || '',
      EVIDENCE_ATTACHMENTS: lead.EVIDENCE_ATTACHMENTS || [],
      LEAD_SCORE: lead.LEAD_SCORE || 0,
      ICP_PRIORITY: lead.ICP_PRIORITY || 'C',
      LEAD_SOURCE: lead.LEAD_SOURCE || 'Otro',
      ENRICHMENT_READY: lead.ENRICHMENT_READY || false,
      OWNER_ID: lead.OWNER_ID || 'IVANA',
      STATE: lead.STATE || 'Nuevo',
      EVIDENCE_COMPLETENESS: lead.EVIDENCE_COMPLETENESS || 0,
      CREATED_AT: new Date().toISOString(),
      UPDATED_AT: new Date().toISOString(),
      ...lead
    };
    // Update local inmediatamente (optimistic)
    setData(prev => ({
      ...prev,
      leads: [newLead, ...prev.leads]
    }));
    // Sync a la nube
    if (isSupabaseConfigured && supabase) {
      supabase.from('leads').insert(leadToRow(newLead)).then(({
        error
      }) => {
        if (error) console.error('[createLead]', error);
      });
    }
    return newLead;
  };
  const updateLead = (id: string, patch: Partial<Lead>) => {
    let updated: Lead | undefined;
    setData(prev => ({
      ...prev,
      leads: prev.leads.map(l => {
        if (l.id !== id) return l;
        updated = {
          ...l,
          ...patch,
          UPDATED_AT: new Date().toISOString()
        };
        return updated;
      })
    }));
    if (isSupabaseConfigured && supabase && updated) {
      supabase.from('leads').update(leadToRow(updated)).eq('id', id).then(({
        error
      }) => {
        if (error) console.error('[updateLead]', error);
      });
    }
  };
  const deleteLead = (id: string) => {
    setData(prev => ({
      ...prev,
      leads: prev.leads.filter(l => l.id !== id),
      timeline: prev.timeline.filter(t => t.leadId !== id),
      events: prev.events.filter(e => e.leadId !== id),
      qaRecords: prev.qaRecords.filter(q => q.leadId !== id)
    }));
    if (isSupabaseConfigured && supabase) {
      // cascade en DB borra timeline/events/qa relacionados
      supabase.from('leads').delete().eq('id', id).then(({
        error
      }) => {
        if (error) console.error('[deleteLead]', error);
      });
    }
  };
  const createEvent = (event: Omit<CalendarEvent, 'id'>) => {
    const newEvent: CalendarEvent = {
      ...event,
      id: `E-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      events: [...prev.events, newEvent]
    }));
    if (isSupabaseConfigured && supabase) {
      supabase.from('calendar_events').insert(eventToRow(newEvent)).then(({
        error
      }) => {
        if (error) console.error('[createEvent]', error);
      });
    }
  };
  const updateEvent = (id: string, patch: Partial<CalendarEvent>) => {
    let updated: CalendarEvent | undefined;
    setData(prev => ({
      ...prev,
      events: prev.events.map(e => {
        if (e.id !== id) return e;
        updated = {
          ...e,
          ...patch
        };
        return updated;
      })
    }));
    if (isSupabaseConfigured && supabase && updated) {
      supabase.from('calendar_events').update(eventToRow(updated)).eq('id', id).then(({
        error
      }) => {
        if (error) console.error('[updateEvent]', error);
      });
    }
  };
  const deleteEvent = (id: string) => {
    setData(prev => ({
      ...prev,
      events: prev.events.filter(e => e.id !== id)
    }));
    if (isSupabaseConfigured && supabase) {
      supabase.from('calendar_events').delete().eq('id', id).then(({
        error
      }) => {
        if (error) console.error('[deleteEvent]', error);
      });
    }
  };
  const addTimelineEvent = (event: Omit<Interaction, 'id'>) => {
    const newEvent: Interaction = {
      ...event,
      id: `T-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      timeline: [newEvent, ...prev.timeline]
    }));
    if (isSupabaseConfigured && supabase) {
      supabase.from('timeline_events').insert(interactionToRow(newEvent)).then(({
        error
      }) => {
        if (error) console.error('[addTimelineEvent]', error);
      });
    }
  };
  const createQaRecord = (record: Omit<QaRecord, 'id'>) => {
    const newRecord: QaRecord = {
      ...record,
      id: `Q-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      qaRecords: [newRecord, ...prev.qaRecords]
    }));
    if (isSupabaseConfigured && supabase) {
      supabase.from('qa_records').insert(qaToRow(newRecord)).then(({
        error
      }) => {
        if (error) console.error('[createQaRecord]', error);
      });
    }
  };
  const resetAll = () => {
    setData(initialState);
    if (isSupabaseConfigured && supabase) {
      // Borra todo el workspace (solo lo invoca el botón "Zona de Peligro")
      const wipe = async () => {
        await supabase.from('qa_records').delete().neq('id', '__none__');
        await supabase.from('timeline_events').delete().neq('id', '__none__');
        await supabase.from('calendar_events').delete().neq('id', '__none__');
        await supabase.from('leads').delete().neq('id', '__none__');
      };
      wipe().catch(err => console.error('[resetAll]', err));
    }
  };
  return <DataStoreContext.Provider value={{
    ...data,
    isSyncing,
    isCloudConnected,
    createLead,
    updateLead,
    deleteLead,
    createEvent,
    updateEvent,
    deleteEvent,
    addTimelineEvent,
    createQaRecord,
    resetAll
  }} data-id="element-776">
      {children}
    </DataStoreContext.Provider>;
}
export const useDataStore = () => {
  const ctx = useContext(DataStoreContext);
  if (!ctx) throw new Error('useDataStore must be used within DataStoreProvider');
  return ctx;
};